import React, { useState, useEffect, useRef } from 'react';
import { Plus, Brain, Clock, Target, Zap, CheckCircle, Play, Pause, RotateCcw, Lightbulb, User, Home, MessageSquare, Send, Bot, Sparkles, Heart, Star, Trash2, Settings, X, Moon, Sun, GripVertical, Calendar, AlertCircle, Timer, Tag, Filter, Mail, Lock, Check, ShieldCheck, MapPin, Pencil, Save, Volume2, Bell, Waves, MessageCircle } from 'lucide-react';

// --- SETUP ---
// NOTE: Sounds are now generated programmatically, so no external files are needed.
// The `quack.mp3` and `completion-sound.mp3` are no longer required.
// -------------

// IMPORTANT for multi-device access (like on your phone):
// 'localhost' only works on the same machine. To connect from another device
// on your network (like your phone), you MUST replace 'localhost' with your
// computer's local network IP address (e.g., 'http://192.168.1.10:3001').
const API_BASE_URL = 'http://api.waddleit.com';

// The URL for the Google Apps Script email verification service.
const EMAIL_VERIFICATION_API_URL = 'https://script.google.com/macros/s/AKfycbye5RCdmDHHGk1nuKbXVCs4P785K1dGKx0T40I-pmEoi5omJ_7i9N6SI3z4GPH7uQgv/exec';

// --- Interfaces ---
interface User {
  id: string;
  email: string;
  name: string;
  isVerified: boolean;
}

interface UserSettings {
  enableDarkMode: boolean;
  enableConfetti: boolean;
  enableSounds: boolean;
  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY';
  timeFormat: '12h' | '24h';
}

interface Task {
  id: number;
  text: string;
  completed: boolean;
  priority: string;
  estimated_time: number;
  actual_time: number;
  category: string;
  deadline?: string;
  tags: string[];
  location?: 'home' | 'work' | 'school' | 'errands';
  timeOfDay?: 'morning' | 'afternoon' | 'night';
  reminder?: string; // ISO string for reminder time
}

interface QuickThought {
  id: number;
  text: string;
  timestamp: string;
  tags: string[];
}

interface VaultItem {
  id: number;
  title: string;
  content: string;
  timestamp: string;
}

interface PondDuck {
  id: string; // YYYY-MM-DD
  date: string;
  tasksCompleted: number;
  variety: number;
}

interface UserData {
  tasks: Task[];
  quickThoughts: QuickThought[];
  vaultItems: VaultItem[];
  pomodoroCount: number;
  lastResetDate: string;
  customTimerDuration: number;
  customBreakDuration: number;
  customTags: string[];
  pondDucks: PondDuck[];
  lastPondResetMonth: number;
  userSettings: UserSettings;
}

interface PopupMessage {
  id: number;
  message: string;
  type: 'mood' | 'energy' | 'general';
  value?: number;
}

interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'ai';
  timestamp: string;
  suggestions?: string[];
}

interface SubTask {
  id: number;
  text: string;
  completed: boolean;
}

// Sound generation
let audioContext: AudioContext | null = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
};

const playCompletionSound = () => {
  try {
    const context = getAudioContext();
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    gainNode.gain.setValueAtTime(0, context.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, context.currentTime + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.00001, context.currentTime + 0.5);

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(440, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(880, context.currentTime + 0.2);
    
    oscillator.start(context.currentTime);
    oscillator.stop(context.currentTime + 0.5);
  } catch (error) {
    console.error("Could not play completion sound:", error);
  }
};

const playQuackSound = () => {
    try {
        const context = getAudioContext();
        const now = context.currentTime;

        const osc = context.createOscillator();
        const gain = context.createGain();
        const bandpass = context.createBiquadFilter();

        bandpass.type = 'bandpass';
        bandpass.frequency.value = 1500;
        bandpass.Q.value = 2;

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.exponentialRampToValueAtTime(700, now + 0.05);
        osc.frequency.exponentialRampToValueAtTime(400, now + 0.15);

        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.5, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

        osc.connect(bandpass);
        bandpass.connect(gain);
        gain.connect(context.destination);

        osc.start(now);
        osc.stop(now + 0.2);
    } catch (error) {
        console.error("Could not play quack sound:", error);
    }
};


const ConfettiComponent = ({ onComplete }: { onComplete: () => void }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const container = canvas.parentElement;
    if (!container) return;

    let width = container.offsetWidth;
    let height = container.offsetHeight;
    canvas.width = width;
    canvas.height = height;

    const pieces: any[] = [];
    const numberOfPieces = 200;
    const duration = 5000;

    const colors = ['#0ea5e9', '#38bdf8', '#7dd3fc', '#fefce8', '#fde68a'];

    function createPiece() {
      const x = Math.random() * width;
      const y = Math.random() * height - height;
      const size = Math.random() * 10 + 5;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const speedX = Math.random() * 10 - 5;
      const speedY = Math.random() * 5 + 2;
      const rotation = Math.random() * 360;
      const rotationSpeed = Math.random() * 10 - 5;

      return { x, y, size, color, speedX, speedY, rotation, rotationSpeed };
    }

    for (let i = 0; i < numberOfPieces; i++) {
      pieces.push(createPiece());
    }

    let startTime = Date.now();
    let animationFrameId: number;

    function animate() {
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime > duration) {
        if (onComplete) onComplete();
        return;
      }
      
      if(ctx) ctx.clearRect(0, 0, width, height);

      pieces.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.rotation += p.rotationSpeed;
        p.speedY += 0.1;
        p.speedX *= 0.99;
        
        if (p.y > height) {
          Object.assign(p, createPiece());
          p.y = -20;
        }
        if (p.x > width + 20 || p.x < -20) {
            Object.assign(p, createPiece());
            p.x = Math.random() * width;
        }
        if(ctx) {
            ctx.save();
            ctx.translate(p.x + p.size / 2, p.y + p.size / 2);
            ctx.rotate(p.rotation * Math.PI / 180);
            ctx.fillStyle = p.color;
            ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
            ctx.restore();
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    }

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [onComplete]);

  return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999 }} />;
};


const VaultComponent = ({ items, onAdd, onDelete, themeClasses, formatDate }: { items: VaultItem[], onAdd: (title: string, content: string) => void, onDelete: (id: number) => void, themeClasses: any, formatDate: (isoString: string) => string }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');

    const handleAdd = () => {
        onAdd(title, content);
        setTitle('');
        setContent('');
    };

    return (
        <div className="space-y-6">
            <div className="text-center">
              <h2 className={`text-2xl font-bold ${themeClasses.text}`}>The Vault</h2>
              <p className={themeClasses.textSecondary}>A secure place for your most important, long-term information.</p>
            </div>

            <div className={`${themeClasses.cardBg} p-6 rounded-2xl shadow-sm ${themeClasses.border}`}>
                <div className="space-y-4">
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g., 'Social Security Number')" className={`w-full px-4 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500`} />
                    <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Content (e.g., '123-45-678')" rows={3} className={`w-full px-4 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500`} ></textarea>
                    <button onClick={handleAdd} disabled={!title.trim() || !content.trim()} className="w-full bg-gradient-to-r from-sky-600 to-cyan-600 text-white px-6 py-3 rounded-xl hover:from-sky-700 hover:to-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium" >
                        <ShieldCheck className="w-5 h-5 mr-2" /> Add to Vault
                    </button>
                </div>
            </div>

            <div className="space-y-3">
                {items.map(item => (
                    <div key={item.id} className={`${themeClasses.cardBg} p-4 rounded-xl shadow-sm ${themeClasses.border}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className={`font-bold ${themeClasses.text}`}>{item.title}</h3>
                                <p className={`mt-1 ${themeClasses.text}`}>{item.content}</p>
                                <p className={`text-xs mt-2 ${themeClasses.textSecondary}`}>{formatDate(item.timestamp)}</p>
                            </div>
                            <button onClick={() => onDelete(item.id)} className={`${themeClasses.textSecondary} hover:text-red-500 p-1`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            {items.length === 0 && (
              <div className="text-center py-12">
                <ShieldCheck className={`w-16 h-16 ${themeClasses.textSecondary} mx-auto mb-4`} />
                <p className={themeClasses.textSecondary}>The Vault is empty. Add something important!</p>
              </div>
            )}
        </div>
    );
};

const ADHDAIAssistant = () => {
  // Authentication state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(true);
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'verify'>('login');
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '' });
  const [authError, setAuthError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sentVerificationCode, setSentVerificationCode] = useState('');
  
  // App state
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTask, setNewTask] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium');
  const [newTaskTime, setNewTaskTime] = useState(15);
  const [newTaskDeadline, setNewTaskDeadline] = useState('');
  const [newTaskTags, setNewTaskTags] = useState<string[]>([]);
  const [newTaskLocation, setNewTaskLocation] = useState<'home' | 'work' | 'school' | 'errands' | undefined>(undefined);
  const [newTaskTimeOfDay, setNewTaskTimeOfDay] = useState<'morning' | 'afternoon' | 'night' | undefined>(undefined);
  const [currentMood, setCurrentMood] = useState(3);
  const [currentEnergy, setCurrentEnergy] = useState(3);
  const [quickThoughts, setQuickThoughts] = useState<QuickThought[]>([]);
  const [newThought, setNewThought] = useState('');
  const [newThoughtTags, setNewThoughtTags] = useState<string[]>([]);
  
  // Settings State
  const [userSettings, setUserSettings] = useState<UserSettings>({
    enableDarkMode: false,
    enableConfetti: true,
    enableSounds: true,
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
  });
  
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [newCustomTag, setNewCustomTag] = useState('');
  const [selectedTagFilter, setSelectedTagFilter] = useState<string>('');
  
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  // Timer state
  const [customTimerDuration, setCustomTimerDuration] = useState(25);
  const [customBreakDuration, setCustomBreakDuration] = useState(5);
  const [timerMinutes, setTimerMinutes] = useState(25);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [isBreak, setIsBreak] = useState(false);
  const [pomodoroCount, setPomodoroCount] = useState(0);
  const [showTimerSettings, setShowTimerSettings] = useState(false);
  
  const [tempWorkDuration, setTempWorkDuration] = useState(25);
  const [tempBreakDuration, setTempBreakDuration] = useState(5);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isAITyping, setIsAITyping] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  // UI state
  const [popupMessages, setPopupMessages] = useState<PopupMessage[]>([]);
  const [draggedTaskIndex, setDraggedTaskIndex] = useState<number | null>(null);
  const [draggedThoughtIndex, setDraggedThoughtIndex] = useState<number | null>(null);
  const [aiSuggestedTask, setAiSuggestedTask] = useState<Task | null>(null);

  // Feature States
  const [showConfetti, setShowConfetti] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<'home' | 'work' | 'school' | 'errands' | null>(null);
  const [vaultItems, setVaultItems] = useState<VaultItem[]>([]);
  const [focusTask, setFocusTask] = useState<Task | null>(null);
  const [focusSubTasks, setFocusSubTasks] = useState<SubTask[]>([]);
  const [isBreakingDownTask, setIsBreakingDownTask] = useState(false);
  const [initialTimerDuration, setInitialTimerDuration] = useState(0);
  const [reminders, setReminders] = useState<NodeJS.Timeout[]>([]);
  const [newTaskReminder, setNewTaskReminder] = useState('');
  const [pondDucks, setPondDucks] = useState<PondDuck[]>([]);
  const [lastPondResetMonth, setLastPondResetMonth] = useState<number>(new Date().getMonth());
  const [bodyDoubleMessages, setBodyDoubleMessages] = useState<ChatMessage[]>([]);
  const [bodyDoubleUserInput, setBodyDoubleUserInput] = useState('');
  const [isBodyDoubleAiTyping, setIsBodyDoubleAiTyping] = useState(false);
  const [bodyDoubleTimer, setBodyDoubleTimer] = useState(25);
  const [bodyDoubleTimeLeft, setBodyDoubleTimeLeft] = useState(0);
  const [isBodyDoubleActive, setIsBodyDoubleActive] = useState(false);
  const [bodyDoubleTask, setBodyDoubleTask] = useState<string>('');
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const bodyDoubleChatEndRef = useRef<HTMLDivElement>(null);


  const moodEmojis = ['😢', '😞', '😐', '😊', '😄'];
  const energyEmojis = ['😴', '😪', '🔋', '⚡', '🚀'];
  const moodDescriptions = ['Very Low', 'Low', 'Neutral', 'Good', 'Excellent'];
  const energyDescriptions = ['Exhausted', 'Tired', 'Moderate', 'Energetic', 'Very High'];

  const formatDate = (isoString: string | undefined): string => {
    if (!isoString) return '';
    const date = new Date(isoString);
    let datePart;
    if (userSettings.dateFormat === 'DD/MM/YYYY') {
        datePart = date.toLocaleDateString('en-GB');
    } else {
        datePart = date.toLocaleDateString('en-US');
    }
    const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: userSettings.timeFormat === '12h' });
    return `${datePart}, ${timePart}`;
  };

  const formatDateOnly = (isoString: string | undefined): string => {
      if (!isoString) return '';
      const date = new Date(isoString);
      if (userSettings.dateFormat === 'DD/MM/YYYY') {
          return date.toLocaleDateString('en-GB');
      }
      return date.toLocaleDateString('en-US');
  }

  const formatTimeOnly = (isoString: string | undefined): string => {
      if (!isoString) return '';
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: userSettings.timeFormat === '12h',
      });
  }


  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPassword = (password: string): boolean => {
    return password.length >= 8;
  };

  const generateVerificationCode = (): string => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendVerificationEmail = async (email: string, code: string): Promise<{ success: boolean }> => {
    try {
      const payload = {
        email,
        code,
        token: authToken, // This will be null during signup, which is fine
      };

      fetch(EMAIL_VERIFICATION_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
        mode: 'no-cors',
      });

      console.log(`Verification email request sent for ${email}.`);
      showPopupMessage(`A verification code has been sent to ${email}!`);
      return { success: true };
    } catch (error) {
      console.error('Failed to send verification email request:', error);
      setAuthError('There was an issue sending the verification email.');
      return { success: false };
    }
  };


  const triggerCelebration = () => {
    if (userSettings.enableConfetti) setShowConfetti(true);
    if (userSettings.enableSounds) playCompletionSound();
  };

  const loadUserData = async (userId: string, token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load user data');
      const data: UserData = await response.json();
      
      setTasks(data.tasks || []);
      data.tasks.forEach(setupReminderForTask);
      setQuickThoughts(data.quickThoughts || []);
      setVaultItems(data.vaultItems || []);
      setPomodoroCount(data.pomodoroCount || 0);
      setCustomTimerDuration(data.customTimerDuration || 25);
      setCustomBreakDuration(data.customBreakDuration || 5);
      setTimerMinutes(data.customTimerDuration || 25);
      setCustomTags(data.customTags || ['work', 'personal', 'health', 'idea', 'reminder', 'urgent', 'daily', 'social', 'exercise', 'errands']);
      setPondDucks(data.pondDucks || []);
      setLastPondResetMonth(data.lastPondResetMonth ?? new Date().getMonth());
      setUserSettings(data.userSettings || {
          enableDarkMode: false,
          enableConfetti: true,
          enableSounds: true,
          dateFormat: 'MM/DD/YYYY',
          timeFormat: '12h',
      });

    } catch (error) {
      console.error('Error loading user data:', error);
      setAuthError('Could not load your data. Please try logging in again.');
      handleLogout();
    }
  };

  const saveUserData = async () => {
      if (!user || !authToken) return;

      const dataToSave: Partial<UserData> = {
          tasks,
          quickThoughts,
          vaultItems,
          pomodoroCount,
          lastResetDate: new Date().toDateString(),
          customTimerDuration,
          customBreakDuration,
          customTags,
          pondDucks,
          lastPondResetMonth,
          userSettings,
      };

      try {
          await fetch(`${API_BASE_URL}/api/users/${user.id}/data`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify(dataToSave),
          });
      } catch (error) {
          console.error('Error saving user data:', error);
          showPopupMessage('Failed to save changes to the server.', 'general');
      }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    const handler = setTimeout(() => {
        saveUserData();
    }, 1000);

    return () => {
        clearTimeout(handler);
    };
  }, [tasks, quickThoughts, vaultItems, pomodoroCount, customTimerDuration, customBreakDuration, userSettings, customTags, pondDucks, user, isLoggedIn, authToken]);


  const handleAuth = async () => {
    setAuthError('');
    try {
        if (authMode === 'login') {
            if (!isValidEmail(authForm.email) || !isValidPassword(authForm.password)) {
                setAuthError('Please enter a valid email and a password of at least 8 characters.');
                return;
            }
            const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: authForm.email, password: authForm.password }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Login failed');

            const loggedInUser: User = { id: data.id, email: data.email, name: data.name, isVerified: true };
            setUser(loggedInUser);
            setAuthToken(data.token);
            setIsLoggedIn(true);
            setShowAuthModal(false);
            await loadUserData(loggedInUser.id, data.token);
             setChatMessages([{
                id: 1,
                text: `Welcome back, ${loggedInUser.name}! I'm Waddle, your companion. How are things feeling today?`,
                sender: 'ai',
                timestamp: new Date().toISOString(),
                suggestions: ["I'm feeling a bit overwhelmed", "I'd like to focus", "I want to plan my day"]
            }]);
            setShowLocationPrompt(true);

        } else if (authMode === 'signup') {
            if (!authForm.name.trim() || !isValidEmail(authForm.email) || !isValidPassword(authForm.password)) {
                setAuthError('Please fill all fields. Password must be at least 8 characters.');
                return;
            }
            
            const code = generateVerificationCode();
            const { success } = await sendVerificationEmail(authForm.email, code);

            if (success) {
                setSentVerificationCode(code);
                setAuthMode('verify');
            } else {
                setAuthError('Failed to send verification email. Please try again later.');
            }

        } else if (authMode === 'verify') {
            if (verificationCode === sentVerificationCode) {
                const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: authForm.name, email: authForm.email, password: authForm.password }),
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || 'Registration failed');

                const newUser: User = { id: data.id, email: data.email, name: data.name, isVerified: true };
                setUser(newUser);
                setAuthToken(data.token);
                setIsLoggedIn(true);
                setShowAuthModal(false);
                setVerificationCode('');
                setSentVerificationCode('');
                await loadUserData(newUser.id, data.token);

                setChatMessages([{
                    id: 1,
                    text: `Welcome ${newUser.name}! I'm Waddle, your companion. Ready to get started?`,
                    sender: 'ai',
                    timestamp: new Date().toISOString(),
                    suggestions: ["Help me organize my tasks", "I need a focus session", "Tell me about brain dumps"]
                }]);
                showPopupMessage(`Account created successfully! Welcome, ${newUser.name}!`);
                setShowLocationPrompt(true);
            } else {
                setAuthError('Invalid verification code. Please try again.');
            }
        }
    } catch (error) {
        if (error instanceof Error) {
            setAuthError(error.message);
        } else {
            setAuthError('An unknown error occurred.');
        }
    }
  };

  const handleLogout = () => {
    setUser(null);
    setAuthToken(null);
    setIsLoggedIn(false);
    setShowAuthModal(true);
    setTasks([]);
    setQuickThoughts([]);
    setVaultItems([]);
    setPondDucks([]);
    setPomodoroCount(0);
    setCustomTags([]);
    setAuthForm({ email: '', password: '', name: '' });
    setVerificationCode('');
    setSentVerificationCode('');
    setActiveTab('dashboard');
    setAuthMode('login');
    setCurrentLocation(null);
    reminders.forEach(clearTimeout);
    setReminders([]);
  };

  const getTimeOfDay = (): 'morning' | 'afternoon' | 'night' => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 18) return 'afternoon';
    return 'night';
  };

  useEffect(() => {
    if (showTimerSettings) {
      setTempWorkDuration(customTimerDuration);
      setTempBreakDuration(customBreakDuration);
    }
  }, [showTimerSettings, customTimerDuration, customBreakDuration]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);
  
  useEffect(() => {
    bodyDoubleChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [bodyDoubleMessages]);

  const showPopupMessage = (message: string, type: 'mood' | 'energy' | 'general' = 'general', value?: number) => {
    const popup: PopupMessage = {
      id: Date.now(),
      message,
      type,
      value
    };
    
    setPopupMessages(prev => [...prev, popup]);
    
    setTimeout(() => {
      setPopupMessages(prev => prev.filter(p => p.id !== popup.id));
    }, 3000);
  };

  const updateMoodEnergy = (mood: number | undefined, energy: number | undefined) => {
    if (mood !== undefined) {
      setCurrentMood(mood);
      const moodMessages = [
        "Take it easy today - small steps count!",
        "Rough patch? That's okay. Let's find something gentle to start with.",
        "Feeling neutral is perfectly fine. What sounds manageable?",
        "Great mood! Perfect time to tackle something meaningful!",
        "Amazing energy! You're ready to conquer your goals!"
      ];
      showPopupMessage(moodMessages[mood - 1], 'mood', mood);
    }
    
    if (energy !== undefined) {
      setCurrentEnergy(energy);
      const energyMessages = [
        "Rest is important. Maybe try a 5-minute gentle task?",
        "Low energy day? Let's pick something easy and rewarding.",
        "Moderate energy - perfect for steady, focused work!",
        "High energy! Great time for challenging tasks!",
        "Super charged! You could tackle your biggest goals right now!"
      ];
      showPopupMessage(energyMessages[energy - 1], 'energy', energy);
      getAITaskSuggestion();
    }
  };

  const getAITaskSuggestion = async () => {
    try {
      const incompleteTasks = tasks.filter(t => !t.completed);
      if (incompleteTasks.length === 0) {
        setAiSuggestedTask(null);
        return;
      }
      
      const timeOfDay = getTimeOfDay();
      
      let suggestedTask = incompleteTasks.find(t => t.location === currentLocation && t.timeOfDay === timeOfDay);

      if (!suggestedTask) {
        suggestedTask = incompleteTasks.find(t => t.location === currentLocation);
      }
      
      if (!suggestedTask) {
        suggestedTask = incompleteTasks.find(t => t.timeOfDay === timeOfDay);
      }
      
      if (!suggestedTask) {
        const moodEnergyScore = (currentMood + currentEnergy) / 2;
        if (moodEnergyScore <= 2) {
            suggestedTask = incompleteTasks.filter(t => t.estimated_time <= 5).sort((a, b) => a.estimated_time - b.estimated_time)[0];
        } else if (moodEnergyScore <= 3) {
            suggestedTask = incompleteTasks.filter(t => t.estimated_time <= 15).sort((a, b) => a.estimated_time - b.estimated_time)[0];
        } else if (moodEnergyScore <= 4) {
            suggestedTask = incompleteTasks.filter(t => t.priority === 'medium' || t.priority === 'high').sort((a, b) => { const p: Record<string, number> = { high: 3, medium: 2, low: 1 }; return p[b.priority] - p[a.priority]; })[0];
        } else {
            suggestedTask = incompleteTasks.filter(t => t.priority === 'high' || t.deadline).sort((a, b) => { if (a.deadline && b.deadline) { return new Date(a.deadline).getTime() - new Date(b.deadline).getTime(); } if (a.deadline) return -1; if (b.deadline) return 1; const p: Record<string, number> = { high: 3, medium: 2, low: 1 }; return p[b.priority] - p[a.priority]; })[0];
        }
      }

      if (!suggestedTask && incompleteTasks.length > 0) {
        suggestedTask = incompleteTasks[0];
      }

      setAiSuggestedTask(suggestedTask);
    } catch (error) {
      console.error('Error getting AI task suggestion:', error);
    }
  };

  useEffect(() => {
    getAITaskSuggestion();
  }, [tasks, currentMood, currentEnergy, currentLocation]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isTimerActive) {
      interval = setInterval(() => {
        if (timerSeconds > 0) {
          setTimerSeconds(timerSeconds - 1);
        } else if (timerMinutes > 0) {
          setTimerMinutes(timerMinutes - 1);
          setTimerSeconds(59);
        } else {
          setIsTimerActive(false);
          if (!isBreak) {
            setPomodoroCount(prev => prev + 1);
            setTimerMinutes(customBreakDuration);
            setIsBreak(true);
            showPopupMessage(`Amazing focus session, ${user?.name}! Time for a well-deserved break.`);
            triggerCelebration();
          } else {
            setTimerMinutes(customTimerDuration);
            setIsBreak(false);
            showPopupMessage(`Break time over, ${user?.name}! Ready for another productive session?`);
          }
          setTimerSeconds(0);
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, timerMinutes, timerSeconds, isBreak, user?.name, customTimerDuration, customBreakDuration]);
  
  // Pond Logic
  useEffect(() => {
    const currentMonth = new Date().getMonth();
    if (currentMonth !== lastPondResetMonth) {
        setPondDucks([]);
        setLastPondResetMonth(currentMonth);
        showPopupMessage("A new month begins! Your pond has been refreshed.");
    }
  }, []);

  const setupReminderForTask = (task: Task) => {
    if (!task.reminder) return;
    const reminderTime = new Date(task.reminder).getTime();
    const now = new Date().getTime();
    const delay = reminderTime - now;

    if (delay > 0) {
      const timerId = setTimeout(() => {
        showPopupMessage(`Reminder: It's time to work on "${task.text}"!`);
        if (userSettings.enableSounds) playQuackSound();
        setReminders(prev => prev.filter(t => t !== timerId));
      }, delay);
      setReminders(prev => [...prev, timerId]);
    }
  }
  
  const addTask = (taskToAdd: Omit<Task, 'id' | 'completed' | 'actual_time' | 'category'>) => {
    if (!taskToAdd.text.trim()) return;

    const task: Task = {
      id: Date.now(),
      text: taskToAdd.text,
      completed: false,
      priority: taskToAdd.priority,
      estimated_time: taskToAdd.estimated_time,
      actual_time: 0,
      category: 'general',
      deadline: taskToAdd.deadline || undefined,
      tags: [...taskToAdd.tags],
      location: taskToAdd.location,
      timeOfDay: taskToAdd.timeOfDay,
      reminder: taskToAdd.reminder
    };
    
    setTasks(prev => [...prev, task]);
    setupReminderForTask(task);
    
    showPopupMessage(`New task added! You're staying organized`);
    triggerCelebration();
  };

  const toggleTask = (id: number) => {
    setTasks(tasks.map(task => {
      if (task.id === id) {
        if (!task.completed) {
          showPopupMessage(`Task completed! Every win counts!`);
          triggerCelebration();
          // Pond logic
          const today = new Date().toISOString().split('T')[0];
          setPondDucks(prevDucks => {
              const existingDuck = prevDucks.find(d => d.id === today);
              if (existingDuck) {
                  return prevDucks.map(d => d.id === today ? { ...d, tasksCompleted: d.tasksCompleted + 1 } : d);
              } else {
                  return [...prevDucks, { id: today, date: new Date().toLocaleDateString(), tasksCompleted: 1, variety: Math.floor(Math.random() * 4) }];
              }
          });
        }
        return { ...task, completed: !task.completed };
      }
      return task;
    }));
  };

  const deleteTask = (id: number) => {
    setTasks(prev => prev.filter(task => task.id !== id));
    showPopupMessage(`Task removed - keeping your list focused!`);
  };

  const updateTask = (id: number, updates: Partial<Task>) => {
    setTasks(prev => prev.map(task => {
      if (task.id === id) {
        const updatedTask = { ...task, ...updates };
        // If reminder is updated, clear old and set new
        if(updates.reminder !== undefined) {
          // Note: In a real app, you'd need a more robust way to find and clear the specific timeout.
          // For simplicity here, we re-setup all reminders.
          reminders.forEach(clearTimeout);
          const newReminders: NodeJS.Timeout[] = [];
          tasks.forEach(t => {
            const tsk = t.id === id ? updatedTask : t;
            if (!tsk.reminder) return;
            const reminderTime = new Date(tsk.reminder).getTime();
            const now = new Date().getTime();
            const delay = reminderTime - now;
            if (delay > 0) {
              const timerId = setTimeout(() => {
                showPopupMessage(`Reminder: "${tsk.text}"`);
                if (userSettings.enableSounds) playQuackSound();
              }, delay);
              newReminders.push(timerId);
            }
          });
          setReminders(newReminders);
        }
        return updatedTask;
      }
      return task;
    }));
    showPopupMessage(`Task updated! Staying flexible!`);
    setEditingTask(null);
  };

  const addQuickThought = () => {
    if (!newThought.trim()) return;

    const thought: QuickThought = {
      id: Date.now(),
      text: newThought,
      timestamp: new Date().toISOString(),
      tags: [...newThoughtTags]
    };
    
    setQuickThoughts(prev => [thought, ...prev]);
    setNewThought('');
    setNewThoughtTags([]);
    showPopupMessage(`Thought captured! Brain feels clearer`);
  };

  const deleteThought = (id: number) => {
    setQuickThoughts(prev => prev.filter(thought => thought.id !== id));
    showPopupMessage(`Thought cleared - making space for new ideas!`);
  };

  const addVaultItem = (title: string, content: string) => {
    if (!title.trim() || !content.trim()) return;
    const newItem: VaultItem = {
      id: Date.now(),
      title,
      content,
      timestamp: new Date().toISOString(),
    };
    setVaultItems(prev => [newItem, ...prev]);
    showPopupMessage(`"${title}" added to The Vault!`);
  };

  const deleteVaultItem = (id: number) => {
    setVaultItems(prev => prev.filter(item => item.id !== id));
    showPopupMessage(`Item removed from The Vault.`);
  };

  const addCustomTag = () => {
    if (!newCustomTag.trim() || customTags.includes(newCustomTag.trim())) return;
    
    const tag = newCustomTag.trim();
    setCustomTags(prev => [...prev, tag]);
    setNewCustomTag('');
    showPopupMessage(`New tag "${tag}" created!`);
  };

  const deleteCustomTag = (tagToDelete: string) => {
    setCustomTags(prev => prev.filter(tag => tag !== tagToDelete));
    
    setTasks(prev => prev.map(task => ({
      ...task,
      tags: task.tags.filter(tag => tag !== tagToDelete)
    })));
    
    setQuickThoughts(prev => prev.map(thought => ({
      ...thought,
      tags: thought.tags.filter(tag => tag !== tagToDelete)
    })));
    
    showPopupMessage(`Tag "${tagToDelete}" removed from all items`);
  };

  const toggleTimer = () => {
    if (!isTimerActive) {
      const durationInSeconds = (isBreak ? customBreakDuration : customTimerDuration) * 60;
      setInitialTimerDuration(durationInSeconds);
    }
    setIsTimerActive(!isTimerActive);
  };

  const resetTimer = () => {
    setIsTimerActive(false);
    const newMinutes = isBreak ? customBreakDuration : customTimerDuration;
    setTimerMinutes(newMinutes);
    setTimerSeconds(0);
    setInitialTimerDuration(newMinutes * 60);
  };

  const updateTimerSettings = () => {
    setCustomTimerDuration(tempWorkDuration);
    setCustomBreakDuration(tempBreakDuration);
    
    if (!isTimerActive) {
      setTimerMinutes(isBreak ? tempBreakDuration : tempWorkDuration);
      setTimerSeconds(0);
    }
    
    setShowTimerSettings(false);
    showPopupMessage(`Timer settings updated! Perfect for your focus style`);
  };

  const sendAIMessage = async (messageText: string, chatSetter: React.Dispatch<React.SetStateAction<ChatMessage[]>>, typingSetter: React.Dispatch<React.SetStateAction<boolean>>) => {
    if (!messageText.trim() || !authToken) return;

    const userMessage: ChatMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    chatSetter(prev => [...prev, userMessage]);
    typingSetter(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          message: messageText,
          context: {
            mood: currentMood,
            energy: currentEnergy
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        const aiMessage: ChatMessage = {
          id: Date.now() + 1,
          text: data.response,
          sender: 'ai',
          timestamp: new Date().toISOString(),
          suggestions: data.suggestions || ["Tell me more", "That's helpful", "What else can you suggest?"]
        };
        chatSetter(prev => [...prev, aiMessage]);
      } else {
        throw new Error('Failed to get AI response from the server');
      }
    } catch (error) {
      console.error('Error sending message to AI:', error);
      const errorMessage: ChatMessage = {
        id: Date.now() + 1,
        text: "I'm having trouble connecting to Waddle right now, Try again in a bit!",
        sender: 'ai',
        timestamp: new Date().toISOString()
      };
      chatSetter(prev => [...prev, errorMessage]);
    } finally {
      typingSetter(false);
    }
  };
  
  const sendAIAutoprompt = async (messageText: string, chatSetter: React.Dispatch<React.SetStateAction<ChatMessage[]>>, typingSetter: React.Dispatch<React.SetStateAction<boolean>>) => {
    if (!messageText.trim() || !authToken) return;

    typingSetter(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          message: messageText,
          context: {
            mood: currentMood,
            energy: currentEnergy,
            isAutoprompt: true
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        const aiMessage: ChatMessage = {
          id: Date.now() + 1,
          text: data.response,
          sender: 'ai',
          timestamp: new Date().toISOString(),
          suggestions: []
        };
        chatSetter(prev => [...prev, aiMessage]);
      } else {
        throw new Error('Failed to get AI response from the server');
      }
    } catch (error) {
      console.error('Error sending auto-prompt to AI:', error);
       const errorMessage: ChatMessage = {
        id: Date.now() + 1,
        text: "I'm having trouble connecting, but I'm still here with you!",
        sender: 'ai',
        timestamp: new Date().toISOString()
      };
      chatSetter(prev => [...prev, errorMessage]);
    } finally {
      typingSetter(false);
    }
  };

  const handleMainChatSend = () => {
    sendAIMessage(newMessage, setChatMessages, setIsAITyping);
    setNewMessage('');
  };

  const getAITaskBreakdown = async (task: Task) => {
    if (!authToken || isBreakingDownTask) return;
    setIsBreakingDownTask(true);
    setFocusSubTasks([]);

    try {
        const response = await fetch(`${API_BASE_URL}/api/ai/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
                message: `You are a supportive AI assistant. Your goal is to make tasks less overwhelming. Break down the following task into a simple, numbered list of small, manageable, and actionable steps. Be encouraging but concise. The task is: "${task.text}"`,
                context: {} 
            })
        });

        if (response.ok) {
            const data = await response.json();
            const steps = data.response
                .split('\n')
                .map((line: string) => line.trim())
                .filter((line: string) => /^\d+\./.test(line))
                .map((line: string) => line.replace(/^\d+\.\s*/, ''));

            const subTasks: SubTask[] = steps.map((step: string, index: number) => ({
                id: Date.now() + index,
                text: step,
                completed: false
            }));
            setFocusSubTasks(subTasks);
        } else {
            throw new Error('Failed to get task breakdown from the server');
        }
    } catch (error) {
        console.error('Error getting task breakdown:', error);
        setFocusSubTasks([{ id: 1, text: "Couldn't break down the task. Just start small!", completed: false }]);
    } finally {
        setIsBreakingDownTask(false);
    }
  };
    
  const startFocusSession = (task: Task) => {
    setFocusTask(task);
    getAITaskBreakdown(task);
    const duration = task.estimated_time > 0 ? task.estimated_time : customTimerDuration;
    setTimerMinutes(duration);
    setTimerSeconds(0);
    setInitialTimerDuration(duration * 60);
    setIsBreak(false);
    setIsTimerActive(false);
  };

  const endFocusSession = () => {
    if (focusTask) {
      const allSubTasksDone = focusSubTasks.every(st => st.completed);
      if (allSubTasksDone) {
        toggleTask(focusTask.id);
      }
    }
    setFocusTask(null);
    setFocusSubTasks([]);
    resetTimer();
  };
    
  const toggleSubTask = (id: number) => {
    setFocusSubTasks(prev => prev.map(st => st.id === id ? { ...st, completed: !st.completed } : st));
  };


  const handleTaskDragStart = (e: React.DragEvent, index: number) => {
    setDraggedTaskIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTaskDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleTaskDrop = (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (draggedTaskIndex === null) return;
      const currentFilteredTasks = filterTasksByTag(tasks);
      const reorderedTasks = [...currentFilteredTasks];
      const [draggedItem] = reorderedTasks.splice(draggedTaskIndex, 1);
      reorderedTasks.splice(dropIndex, 0, draggedItem);
      const newOrderMap = new Map(reorderedTasks.map((task, index) => [task.id, index]));
      const newTasksState = [...tasks].sort((a, b) => {
          const orderA = newOrderMap.get(a.id);
          const orderB = newOrderMap.get(b.id);
          if (orderA !== undefined && orderB !== undefined) {
              return orderA - orderB;
          }
          return 0; 
      });

      setTasks(newTasksState);
      setDraggedTaskIndex(null);
      showPopupMessage(`Tasks reordered! Great organization!`);
  };

  const handleThoughtDragStart = (e: React.DragEvent, index: number) => {
    setDraggedThoughtIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleThoughtDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedThoughtIndex === null) return;
    const newThoughts = [...quickThoughts];
    const draggedThought = newThoughts[draggedThoughtIndex];
    newThoughts.splice(draggedThoughtIndex, 1);
    newThoughts.splice(dropIndex, 0, draggedThought);
    setQuickThoughts(newThoughts);
    setDraggedThoughtIndex(null);
    showPopupMessage(`Thoughts reordered!`);
  };
  
    // Body Double Logic
  const startBodyDouble = () => {
    setBodyDoubleTimeLeft(bodyDoubleTimer * 60);
    setIsBodyDoubleActive(true);
    const taskText = bodyDoubleTask ? `on "${bodyDoubleTask}"` : `on your task`;
    setBodyDoubleMessages([{
        id: Date.now(),
        sender: 'ai',
        text: `Okay, let's do this! Time to focus ${taskText}. I'm here with you for the next ${bodyDoubleTimer} minutes. Let me know how it's going!`,
        timestamp: new Date().toISOString()
    }]);
    resetInactivityTimer();
  };

  const stopBodyDouble = () => {
    setIsBodyDoubleActive(false);
    if(inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    setBodyDoubleMessages(prev => [...prev, {
        id: Date.now(),
        sender: 'ai',
        text: `Great session! You did it. Take a moment to stretch and be proud of your focus.`,
        timestamp: new Date().toISOString()
    }]);
  };
  
  const resetInactivityTimer = () => {
    if(inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
        const prompt = `You are Waddle, a calm and supportive companion. The user has been quiet for a bit during a focus session. Gently check in on them. A good message would be like: "Just checking in softly. How are things flowing?" or "Hey, just a gentle check-in. Is there anything you need?" Keep it encouraging and not demanding. It is very important you remember your name is Waddle.`;
        sendAIAutoprompt(prompt, setBodyDoubleMessages, setIsBodyDoubleAiTyping);
    }, 4 * 60 * 1000); // 4 minutes
  };
  
  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (isBodyDoubleActive && bodyDoubleTimeLeft > 0) {
        timerId = setInterval(() => {
            setBodyDoubleTimeLeft(prev => prev - 1);
        }, 1000);
    } else if (isBodyDoubleActive && bodyDoubleTimeLeft <= 0) {
        stopBodyDouble();
        triggerCelebration();
    }
    return () => clearInterval(timerId);
  }, [isBodyDoubleActive, bodyDoubleTimeLeft]);

  const handleBodyDoubleSend = () => {
    sendAIMessage(bodyDoubleUserInput, setBodyDoubleMessages, setIsBodyDoubleAiTyping);
    setBodyDoubleUserInput('');
    resetInactivityTimer();
  };


  const renderPopups = () => {
    return (
      <div className="fixed top-4 right-4 z-40 space-y-2 pointer-events-none">
        {popupMessages.map(popup => (
          <div
            key={popup.id}
            className={`
              transform transition-all duration-300 ease-out
              bg-white dark:bg-gray-800 rounded-xl shadow-lg border
              px-4 py-3 max-w-sm animate-pulse
              ${popup.type === 'mood' ? 'border-sky-200 dark:border-sky-700' : ''}
              ${popup.type === 'energy' ? 'border-green-200 dark:border-green-700' : ''}
              ${popup.type === 'general' ? 'border-gray-200 dark:border-gray-700' : ''}
            `}
          >
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                popup.type === 'mood' ? 'bg-sky-500' : 
                popup.type === 'energy' ? 'bg-green-500' : 
                'bg-gray-500'
              }`}></div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {popup.message}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPersistentTimer = () => {
    if (!isTimerActive) return null;

    return (
      <div className="fixed bottom-4 right-4 z-50 bg-gradient-to-r from-sky-600 to-cyan-600 text-white p-4 rounded-2xl shadow-2xl animate-pulse">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Timer className="w-8 h-8 animate-spin" style={{ animationDuration: '2s' }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
            </div>
          </div>
          <div>
            <div className="text-lg font-mono font-bold">
              {String(timerMinutes).padStart(2, '0')}:{String(timerSeconds).padStart(2, '0')}
            </div>
            <div className="text-xs opacity-90">
              {isBreak ? 'Break' : 'Focus'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const themeClasses = {
    bg: userSettings.enableDarkMode ? 'bg-gray-900' : 'bg-eggshell',
    cardBg: userSettings.enableDarkMode ? 'bg-gray-800' : 'bg-beige-card',
    text: userSettings.enableDarkMode ? 'text-gray-100' : 'text-gray-800',
    textSecondary: userSettings.enableDarkMode ? 'text-gray-300' : 'text-gray-600',
    border: userSettings.enableDarkMode ? 'border-gray-700' : 'border-gray-200',
    sidebarBg: userSettings.enableDarkMode ? 'bg-gray-800' : 'bg-white',
  };

  const getSuggestedTask = () => {
    return aiSuggestedTask;
  };

  const getActionableThoughts = () => {
    return quickThoughts.filter(thought => 
      thought.text.toLowerCase().includes('remember') || 
      thought.text.toLowerCase().includes('idea') ||
      thought.text.toLowerCase().includes('need to') ||
      thought.text.toLowerCase().includes('should') ||
      thought.text.toLowerCase().includes('must') ||
      thought.text.toLowerCase().includes('todo') ||
      thought.text.toLowerCase().includes('call') ||
      thought.text.toLowerCase().includes('buy') ||
      thought.text.toLowerCase().includes('pick up') ||
      thought.tags.some(tag => ['reminder', 'todo', 'urgent', 'important'].includes(tag.toLowerCase()))
    ).slice(0, 3);
  };

  const convertThoughtToTask = (thought: QuickThought) => {
    const taskData = {
      text: thought.text,
      priority: thought.tags.includes('urgent') ? 'high' : 'medium',
      estimated_time: 15,
      tags: [...thought.tags, 'from-braindump'],
      deadline: undefined,
      location: undefined,
      timeOfDay: undefined,
      reminder: undefined,
    };
    addTask(taskData);
    setQuickThoughts(prev => prev.filter(t => t.id !== thought.id));
    showPopupMessage(`Thought converted to task! Great organization!`);
    setActiveTab('tasks');
  };

  const TagSelector = ({ 
    selectedTags, 
    onTagChange, 
    placeholder = "Add tags..." 
  }: { 
    selectedTags: string[], 
    onTagChange: (tags: string[]) => void,
    placeholder?: string 
  }) => {
    const [showTagDropdown, setShowTagDropdown] = useState(false);

    const toggleTag = (tag: string) => {
      if (selectedTags.includes(tag)) {
        onTagChange(selectedTags.filter(t => t !== tag));
      } else {
        onTagChange([...selectedTags, tag]);
      }
    };

    return (
      <div className="relative">
        <div 
          className={`flex items-center flex-wrap gap-2 p-2 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg cursor-pointer min-h-[40px]`}
          onClick={() => setShowTagDropdown(!showTagDropdown)}
        >
          {selectedTags.length === 0 ? (
            <span className={`${themeClasses.textSecondary} text-sm`}>{placeholder}</span>
          ) : (
            selectedTags.map(tag => (
              <span
                key={tag}
                className="bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 px-2 py-1 rounded-full text-xs flex items-center"
              >
                {tag}
                <X 
                  className="w-3 h-3 ml-1 cursor-pointer hover:text-red-500" 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTag(tag);
                  }}
                />
              </span>
            ))
          )}
          <Tag className="w-4 h-4 ml-auto text-gray-400" />
        </div>
        
        {showTagDropdown && (
          <div className={`absolute z-10 mt-1 w-full max-h-40 overflow-y-auto ${themeClasses.cardBg} ${themeClasses.border} rounded-lg shadow-lg`}>
            {customTags.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between ${
                  selectedTags.includes(tag) ? 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300' : themeClasses.text
                }`}
              >
                {tag}
                {selectedTags.includes(tag) && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const filterTasksByTag = (tasks: Task[]) => {
    if (!selectedTagFilter) return tasks;
    return tasks.filter(task => task.tags.includes(selectedTagFilter));
  };

  const filterThoughtsByTag = (thoughts: QuickThought[]) => {
    if (!selectedTagFilter) return thoughts;
    return thoughts.filter(thought => thought.tags.includes(selectedTagFilter));
  };

  const renderTimerSettingsModal = () => {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className={`${themeClasses.cardBg} rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-bold ${themeClasses.text}`}>Timer Settings</h3>
            <button 
              onClick={() => setShowTimerSettings(false)}
              className={`${themeClasses.textSecondary} hover:${themeClasses.text}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                Focus Duration (minutes)
              </label>
              <input
                type="number"
                min="1"
                max="90"
                value={tempWorkDuration}
                onChange={(e) => setTempWorkDuration(parseInt(e.target.value) || 25)}
                className={`w-full px-3 py-2 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500`}
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                Break Duration (minutes)
              </label>
              <input
                type="number"
                min="1"
                max="30"
                value={tempBreakDuration}
                onChange={(e) => setTempBreakDuration(parseInt(e.target.value) || 5)}
                className={`w-full px-3 py-2 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500`}
              />
            </div>
          </div>
          
          <div className="flex space-x-3 mt-6">
            <button
              onClick={() => setShowTimerSettings(false)}
              className={`flex-1 px-4 py-2 ${themeClasses.border} ${themeClasses.text} rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700`}
            >
              Cancel
            </button>
            <button
              onClick={updateTimerSettings}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAuthModal = () => {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-sky-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Welcome to Waddle It</h2>
            <p className="text-gray-600 dark:text-gray-300 mt-2">Your Focus Companion</p>
          </div>

          <div className="space-y-4">
            {authMode === 'signup' && (
              <div className="relative">
                <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Full Name"
                  value={authForm.name}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
            )}
            
            {authMode !== 'verify' && (
              <>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    placeholder="Valid Email Address"
                    value={authForm.email}
                    onChange={(e) => setAuthForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    placeholder="Password (8+ characters)"
                    value={authForm.password}
                    onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
                  />
                </div>
              </>
            )}

            {authMode === 'verify' && (
              <div>
                <p className="text-center text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Enter the 6-digit code sent to {authForm.email}
                </p>
                <input
                  type="text"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                  maxLength={6}
                  onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
                />
              </div>
            )}
            
            {authError && (
              <p className="text-red-500 text-sm text-center">{authError}</p>
            )}
            
            <button
              onClick={handleAuth}
              className="w-full bg-gradient-to-r from-sky-600 to-cyan-600 text-white py-3 rounded-lg hover:from-sky-700 hover:to-cyan-700 transition-colors font-medium"
            >
              {authMode === 'login' ? 'Sign In' : authMode === 'signup' ? 'Send Verification Code' : 'Verify Email'}
            </button>
            
            {authMode !== 'verify' && (
              <div className="text-center">
                <button
                  onClick={() => {
                    setAuthMode(authMode === 'login' ? 'signup' : 'login');
                    setAuthError('');
                  }}
                  className="text-sky-600 hover:text-sky-700 text-sm"
                >
                  {authMode === 'login' 
                    ? "Don't have an account? Sign up" 
                    : "Already have an account? Sign in"
                  }
                </button>
              </div>
            )}
            
            {authMode === 'verify' && (
              <div className="text-center">
                <button
                  onClick={() => setAuthMode('signup')}
                  className="text-sky-600 hover:text-sky-700 text-sm"
                >
                  Back to signup
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderLocationPromptModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className={`${themeClasses.cardBg} rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl`}>
            <h3 className={`text-xl font-bold ${themeClasses.text} text-center mb-4`}>Where are you now?</h3>
            <p className={`${themeClasses.textSecondary} text-center text-sm mb-6`}>This helps suggest relevant tasks.</p>
            <div className="grid grid-cols-2 gap-4">
                {(['home', 'work', 'school', 'errands'] as const).map(loc => (
                    <button
                        key={loc}
                        onClick={() => {
                            setCurrentLocation(loc);
                            setShowLocationPrompt(false);
                            showPopupMessage(`Location set to ${loc}. Ready to focus!`);
                        }}
                        className="flex flex-col items-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-colors"
                    >
                        {loc === 'home' && <Home className="w-8 h-8 text-sky-500 mb-2"/>}
                        {loc === 'work' && <Zap className="w-8 h-8 text-sky-500 mb-2"/>}
                        {loc === 'school' && <Brain className="w-8 h-8 text-sky-500 mb-2"/>}
                        {loc === 'errands' && <Target className="w-8 h-8 text-sky-500 mb-2"/>}
                        <span className={`capitalize font-medium ${themeClasses.text}`}>{loc}</span>
                    </button>
                ))}
            </div>
        </div>
    </div>
  );

  const renderDashboard = () => {
    const suggestedTask = getSuggestedTask();
    const actionableThoughts = getActionableThoughts();
    const urgentTasks = tasks.filter(t => !t.completed && t.deadline).slice(0, 3);
    const timeOfDay = getTimeOfDay();
    const greeting = `Good ${timeOfDay.charAt(0).toUpperCase() + timeOfDay.slice(1)}`;
    
    return (
      <div className="space-y-8">
        <div className="relative overflow-hidden bg-gradient-to-br from-sky-600 via-cyan-600 to-teal-700 text-white p-8 rounded-3xl">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-2xl transform -translate-x-24 translate-y-24"></div>
          
          <div className="relative">
            <h2 className="text-3xl font-bold mb-2">{greeting}, {user?.name}!</h2>
            <p className="text-white/90 text-lg">Ready to make today amazing?</p>
            
            <div className="mt-6 flex flex-wrap gap-3">
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                <Target className="inline w-4 h-4 mr-2" />
                {tasks.filter(t => !t.completed).length} active tasks
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                <Zap className="inline w-4 h-4 mr-2" />
                {pomodoroCount} focus sessions today
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                {moodEmojis[currentMood - 1]} {moodDescriptions[currentMood - 1]} mood
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                {energyEmojis[currentEnergy - 1]} {energyDescriptions[currentEnergy - 1]} energy
              </div>
              {currentLocation && <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium flex items-center"><MapPin className="inline w-4 h-4 mr-2" /> <span className="capitalize">{currentLocation}</span></div>}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {suggestedTask && (
            <div className="bg-gradient-to-br from-sky-50 to-cyan-100 dark:from-sky-900/20 dark:to-cyan-900/20 p-6 rounded-2xl border border-sky-200 dark:border-sky-800">
              <h3 className="text-lg font-bold text-sky-900 dark:text-sky-100 mb-3 flex items-center">
                <Target className="w-5 h-5 mr-2" />
                AI Suggested Task
              </h3>
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-4 rounded-xl">
                <p className="font-medium text-gray-800 dark:text-gray-200 mb-2">{suggestedTask.text}</p>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex space-x-2 flex-wrap gap-1">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      suggestedTask.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                      suggestedTask.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                      'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {suggestedTask.priority}
                    </span>
                    <span className="text-xs bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 px-2 py-1 rounded-full">
                      ~{suggestedTask.estimated_time}min
                    </span>
                    {suggestedTask.deadline && (
                      <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-1 rounded-full flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDateOnly(suggestedTask.deadline)}
                      </span>
                    )}
                    {suggestedTask.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="text-xs bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 px-2 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => startFocusSession(suggestedTask)}
                    className="bg-sky-600 hover:bg-sky-700 text-white px-3 py-1 rounded-lg text-sm font-medium transition-colors"
                  >
                    Start Focus
                  </button>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Suggested for your current state.
                </p>
              </div>
            </div>
          )}

          {urgentTasks.length > 0 && (
            <div className="bg-gradient-to-br from-red-50 to-pink-100 dark:from-red-900/20 dark:to-pink-900/20 p-6 rounded-2xl border border-red-200 dark:border-red-800">
              <h3 className="text-lg font-bold text-red-900 dark:text-red-100 mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                Upcoming Deadlines
              </h3>
              <div className="space-y-2">
                {urgentTasks.map(task => (
                  <div key={task.id} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-3 rounded-lg">
                    <p className="text-sm text-gray-800 dark:text-gray-200 font-medium mb-1">{task.text}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-red-600 dark:text-red-400 flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        Due: {formatDateOnly(task.deadline)}
                      </span>
                      <button
                        onClick={() => setActiveTab('tasks')}
                        className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
                      >
                        View Task
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {actionableThoughts.length > 0 && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 p-6 rounded-2xl border border-amber-200 dark:border-amber-800">
              <h3 className="text-lg font-bold text-amber-900 dark:text-amber-100 mb-3 flex items-center">
                <Lightbulb className="w-5 h-5 mr-2" />
                Turn Thoughts into Action
              </h3>
              <div className="space-y-2">
                {actionableThoughts.map(thought => (
                  <div key={thought.id} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-3 rounded-lg">
                    <p className="text-sm text-gray-800 dark:text-gray-200 mb-2">{thought.text}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-1">
                        {thought.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-1 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={() => convertThoughtToTask(thought)}
                        className="bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
                      >
                        Make it a task
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-green-50 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 p-6 rounded-2xl border border-green-200 dark:border-green-800">
          <h3 className="text-lg font-bold text-green-900 dark:text-green-100 mb-4 flex items-center">
            <Heart className="w-5 h-5 mr-2" />
            How are you feeling?
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-green-800 dark:text-green-200 mb-3">
                Mood: {moodDescriptions[currentMood - 1]}
              </label>
              <div className="flex space-x-2">
                {[1, 2, 3, 4, 5].map(level => (
                  <button
                    key={level}
                    onClick={() => updateMoodEnergy(level, undefined)}
                    className={`p-3 rounded-xl text-2xl transition-all ${
                      currentMood === level 
                        ? 'bg-white dark:bg-gray-700 shadow-lg scale-110' 
                        : 'bg-white/50 dark:bg-gray-800/50 hover:bg-white/70 dark:hover:bg-gray-700/70'
                    }`}
                    title={moodDescriptions[level - 1]}
                  >
                    {moodEmojis[level - 1]}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-green-800 dark:text-green-200 mb-3">
                Energy: {energyDescriptions[currentEnergy - 1]}
              </label>
              <div className="flex space-x-2">
                {[1, 2, 3, 4, 5].map(level => (
                  <button
                    key={level}
                    onClick={() => updateMoodEnergy(undefined, level)}
                    className={`p-3 rounded-xl text-2xl transition-all ${
                      currentEnergy === level 
                        ? 'bg-white dark:bg-gray-700 shadow-lg scale-110' 
                        : 'bg-white/50 dark:bg-gray-800/50 hover:bg-white/70 dark:hover:bg-gray-700/70'
                    }`}
                    title={energyDescriptions[level - 1]}
                  >
                    {energyEmojis[level - 1]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`${themeClasses.cardBg} p-4 rounded-xl shadow-sm ${themeClasses.border}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${themeClasses.textSecondary}`}>Tasks Done</p>
                <p className="text-2xl font-bold text-green-600">{tasks.filter(t => t.completed).length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className={`${themeClasses.cardBg} p-4 rounded-xl shadow-sm ${themeClasses.border}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${themeClasses.textSecondary}`}>Focus Time</p>
                <p className="text-2xl font-bold text-sky-600">{pomodoroCount * customTimerDuration}m</p>
              </div>
              <Clock className="w-8 h-8 text-sky-500" />
            </div>
          </div>
          
          <div className={`${themeClasses.cardBg} p-4 rounded-xl shadow-sm ${themeClasses.border}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${themeClasses.textSecondary}`}>Thoughts</p>
                <p className="text-2xl font-bold text-purple-600">{quickThoughts.length}</p>
              </div>
              <Brain className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className={`${themeClasses.cardBg} p-4 rounded-xl shadow-sm ${themeClasses.border}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${themeClasses.textSecondary}`}>Vault Items</p>
                <p className="text-2xl font-bold text-orange-600">{vaultItems.length}</p>
              </div>
              <ShieldCheck className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTaskEditModal = () => {
    if (!editingTask) return null;

    const handleSave = () => {
        updateTask(editingTask.id, editingTask);
    };
    
    const handleFieldChange = (field: keyof Task, value: any) => {
        setEditingTask(prev => prev ? { ...prev, [field]: value } : null);
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className={`${themeClasses.cardBg} rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl`}>
                <div className="flex items-center justify-between mb-6">
                    <h3 className={`text-xl font-bold ${themeClasses.text}`}>Edit Task</h3>
                    <button onClick={() => setEditingTask(null)} className={`${themeClasses.textSecondary} hover:${themeClasses.text}`}><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-4">
                    <input
                        type="text"
                        value={editingTask.text}
                        onChange={(e) => handleFieldChange('text', e.target.value)}
                        className={`w-full px-4 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500`}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Priority</label>
                            <select value={editingTask.priority} onChange={(e) => handleFieldChange('priority', e.target.value)} className={`w-full px-3 py-2 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg`}>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                            </select>
                        </div>
                        <div>
                            <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Time (min)</label>
                            <input type="number" value={editingTask.estimated_time} onChange={(e) => handleFieldChange('estimated_time', parseInt(e.target.value))} className={`w-full px-3 py-2 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg`} />
                        </div>
                        <div>
                            <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Deadline</label>
                            <input type="date" value={editingTask.deadline?.split('T')[0] || ''} onChange={(e) => handleFieldChange('deadline', e.target.value)} className={`w-full px-3 py-2 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg`} />
                        </div>
                        <div>
                            <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Reminder</label>
                             <input type="datetime-local" value={editingTask.reminder ? editingTask.reminder.slice(0, 16) : ''} onChange={(e) => handleFieldChange('reminder', e.target.value)} className={`w-full px-3 py-2 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg`} />
                        </div>
                    </div>
                     <div>
                        <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Tags</label>
                        <TagSelector selectedTags={editingTask.tags} onTagChange={(tags) => handleFieldChange('tags', tags)} />
                    </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                    <button onClick={() => setEditingTask(null)} className={`px-4 py-2 ${themeClasses.border} rounded-lg`}>Cancel</button>
                    <button onClick={handleSave} className="bg-gradient-to-r from-sky-600 to-cyan-600 text-white px-4 py-2 rounded-lg">Save Changes</button>
                </div>
            </div>
        </div>
    );
};


  const renderTasks = () => {
    const filteredTasks = filterTasksByTag(tasks);
    
    const handleAddTask = () => {
        addTask({
            text: newTask,
            priority: newTaskPriority,
            estimated_time: newTaskTime,
            deadline: newTaskDeadline,
            tags: newTaskTags,
            location: newTaskLocation,
            timeOfDay: newTaskTimeOfDay,
            reminder: newTaskReminder,
        });
        setNewTask('');
        setNewTaskPriority('medium');
        setNewTaskTime(15);
        setNewTaskDeadline('');
        setNewTaskTags([]);
        setNewTaskLocation(undefined);
        setNewTaskTimeOfDay(undefined);
        setNewTaskReminder('');
    };

    return (
      <div className="space-y-6">
        {editingTask && renderTaskEditModal()}
        <div className="flex items-center justify-between">
          <h2 className={`text-2xl font-bold ${themeClasses.text}`}>Tasks</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={selectedTagFilter}
                onChange={(e) => setSelectedTagFilter(e.target.value)}
                className={`text-sm ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg px-2 py-1`}
              >
                <option value="">All tags</option>
                {customTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
            <div className={`text-sm ${themeClasses.textSecondary}`}>
              {filteredTasks.filter(t => !t.completed).length} remaining
            </div>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} p-8 rounded-2xl shadow-lg ${themeClasses.border} border-2 border-dashed border-sky-300 dark:border-sky-600 bg-gradient-to-r from-sky-50/50 to-cyan-50/50 dark:from-sky-900/20 dark:to-cyan-900/20`}>
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-sky-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-sky-900 dark:text-sky-100 mb-2">Add a New Task</h3>
            <p className="text-sky-700 dark:text-sky-300">Break down your goals into manageable steps</p>
          </div>
          
          <div className="space-y-4">
            <input
              type="text"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              placeholder="What needs to get done? (e.g., 'Call doctor for checkup')"
              className={`w-full px-6 py-4 text-lg ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 shadow-sm`}
              onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>Priority</label>
                <select value={newTaskPriority} onChange={(e) => setNewTaskPriority(e.target.value)} className={`w-full px-3 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>Time (min)</label>
                <input type="number" value={newTaskTime} onChange={(e) => setNewTaskTime(parseInt(e.target.value) || 15)} min="1" className={`w-full px-3 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`} />
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>Deadline</label>
                <input type="date" value={newTaskDeadline} onChange={(e) => setNewTaskDeadline(e.target.value)} className={`w-full px-3 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`} />
              </div>

               <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2 flex items-center`}><Bell className="w-4 h-4 mr-1"/>Reminder</label>
                <input type="datetime-local" value={newTaskReminder} onChange={(e) => setNewTaskReminder(e.target.value)} className={`w-full px-3 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`} />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>Location</label>
                <select value={newTaskLocation || ''} onChange={(e) => setNewTaskLocation(e.target.value as any)} className={`w-full px-3 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`}>
                    <option value="">None</option>
                    <option value="home">Home</option>
                    <option value="work">Work</option>
                    <option value="school">School</option>
                    <option value="errands">Errands</option>
                </select>
              </div>
              <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>Time of Day</label>
                  <select value={newTaskTimeOfDay || ''} onChange={(e) => setNewTaskTimeOfDay(e.target.value as any)} className={`w-full px-3 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`}>
                      <option value="">Any</option>
                      <option value="morning">Morning</option>
                      <option value="afternoon">Afternoon</option>
                      <option value="night">Night</option>
                  </select>
              </div>

              <div className="md:col-span-2">
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>Tags</label>
                <TagSelector selectedTags={newTaskTags} onTagChange={setNewTaskTags} placeholder="Select tags..." />
              </div>
            </div>
            
            <button onClick={handleAddTask} disabled={!newTask.trim()} className="w-full bg-gradient-to-r from-sky-600 to-cyan-600 text-white px-6 py-4 rounded-xl hover:from-sky-700 hover:to-cyan-700 transition-colors flex items-center justify-center text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
              <Plus className="w-6 h-6 mr-2" /> Add Task
            </button>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} p-4 rounded-xl shadow-sm ${themeClasses.border}`}>
          <h3 className={`text-lg font-medium ${themeClasses.text} mb-3`}>Manage Tags</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {customTags.map(tag => (
              <div key={tag} className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-full px-3 py-1">
                <Tag className="w-3 h-3 mr-1 text-gray-500 dark:text-gray-400" />
                <span className={`text-sm ${themeClasses.text}`}>{tag}</span>
                <button onClick={() => deleteCustomTag(tag)} className="ml-2 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex space-x-2">
            <input
              type="text"
              value={newCustomTag}
              onChange={(e) => setNewCustomTag(e.target.value)}
              placeholder="Add new tag..."
              className={`flex-1 px-3 py-2 text-sm ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`}
              onKeyPress={(e) => e.key === 'Enter' && addCustomTag()}
            />
            <button onClick={addCustomTag} className="bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 rounded-lg text-sm">
              Add Tag
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {filteredTasks.map((task, index) => (
            <div
              key={task.id}
              draggable
              onDragStart={(e) => handleTaskDragStart(e, index)}
              onDragOver={handleTaskDragOver}
              onDrop={(e) => handleTaskDrop(e, index)}
              className={`${themeClasses.cardBg} p-4 rounded-xl shadow-sm ${themeClasses.border} ${
                task.completed ? 'opacity-60' : ''
              } cursor-move hover:shadow-md transition-all`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1">
                  <GripVertical className={`w-4 h-4 ${themeClasses.textSecondary} cursor-grab`} />
                  <button onClick={() => toggleTask(task.id)} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${ task.completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-500' }`}>
                    {task.completed && <CheckCircle className="w-4 h-4" />}
                  </button>
                  <div className="flex-1">
                    <p className={`font-medium ${task.completed ? `line-through ${themeClasses.textSecondary}` : themeClasses.text}`}>
                      {task.text}
                    </p>
                    <div className="flex items-center space-x-2 mt-1 flex-wrap gap-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${ task.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' }`}>
                        {task.priority}
                      </span>
                      <span className={`text-xs ${themeClasses.textSecondary}`}>~{task.estimated_time}min</span>
                      {task.location && <span className={`text-xs ${themeClasses.textSecondary} capitalize flex items-center`}><MapPin className="w-3 h-3 mr-1"/>{task.location}</span>}
                      {task.deadline && <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-1 rounded-full flex items-center"><Calendar className="w-3 h-3 mr-1" />{formatDateOnly(task.deadline)}</span>}
                      {task.reminder && <span className="text-xs bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 px-2 py-1 rounded-full flex items-center"><Bell className="w-3 h-3 mr-1" />{formatTimeOnly(task.reminder)}</span>}
                      {task.tags.map(tag => (
                        <span key={tag} className="text-xs bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 px-2 py-1 rounded-full">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => startFocusSession(task)} disabled={task.completed} className="flex items-center space-x-1 bg-sky-500 hover:bg-sky-600 text-white px-3 py-1 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <Target className="w-3 h-3"/>
                      <span>Focus</span>
                  </button>
                  <button onClick={() => setEditingTask(task)} className={`${themeClasses.textSecondary} hover:text-sky-500 p-1 transition-colors`} >
                      <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteTask(task.id)} className={`${themeClasses.textSecondary} hover:text-red-500 p-1 transition-colors`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <Target className={`w-16 h-16 ${themeClasses.textSecondary} mx-auto mb-4`} />
            <p className={themeClasses.textSecondary}>
              {selectedTagFilter ? `No tasks found with tag "${selectedTagFilter}"` : 'No tasks yet. Add one above to get started!'}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderFocusTimer = () => {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className={`text-2xl font-bold ${themeClasses.text} mb-2`}>Focus Timer</h2>
          <p className={themeClasses.textSecondary}>
            {isBreak ? 'Take a break!' : 'Time to focus!'}
          </p>
        </div>

        <div className="relative">
          <div className="bg-gradient-to-br from-sky-500 via-cyan-600 to-teal-700 text-white p-12 rounded-3xl text-center">
            <div className="text-8xl font-mono font-bold mb-4">
              {String(timerMinutes).padStart(2, '0')}:{String(timerSeconds).padStart(2, '0')}
            </div>
            <p className="text-xl opacity-90 mb-8">
              {isBreak ? 'Break Time' : 'Focus Time'}
            </p>
            
            <div className="flex justify-center space-x-4">
              <button onClick={toggleTimer} className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-4 rounded-2xl transition-colors">
                {isTimerActive ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
              </button>
              <button onClick={resetTimer} className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-4 rounded-2xl transition-colors">
                <RotateCcw className="w-8 h-8" />
              </button>
              <button onClick={() => setShowTimerSettings(true)} className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-4 rounded-2xl transition-colors">
                <Settings className="w-8 h-8" />
              </button>
            </div>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} p-6 rounded-2xl shadow-sm ${themeClasses.border} text-center`}>
          <div className="flex items-center justify-center space-x-2 mb-3">
            <Zap className="w-6 h-6 text-orange-500" />
            <h3 className={`text-lg font-bold ${themeClasses.text}`}>Today's Sessions</h3>
          </div>
          <div className="text-3xl font-bold text-orange-600 mb-2">{pomodoroCount}</div>
          <div className="flex justify-center space-x-1">
            {[...Array(8)].map((_, i) => (
              <div key={i} className={`w-3 h-3 rounded-full ${i < pomodoroCount ? 'bg-orange-500' : 'bg-gray-200 dark:bg-gray-600'}`} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderBrainDump = () => {
    const filteredThoughts = filterThoughtsByTag(quickThoughts);
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className={`text-2xl font-bold ${themeClasses.text}`}>Brain Dump</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={selectedTagFilter}
                onChange={(e) => setSelectedTagFilter(e.target.value)}
                className={`text-sm ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg px-2 py-1`}
              >
                <option value="">All tags</option>
                {customTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
            <div className={`text-sm ${themeClasses.textSecondary}`}>
              {filteredThoughts.length} thoughts captured
            </div>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} p-6 rounded-2xl shadow-sm ${themeClasses.border}`}>
          <div className="space-y-4">
            <input
              type="text"
              value={newThought}
              onChange={(e) => setNewThought(e.target.value)}
              placeholder="What's on your mind? Capture any thought, idea, or reminder..."
              className={`w-full px-4 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500`}
              onKeyPress={(e) => e.key === 'Enter' && addQuickThought()}
            />
            
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <TagSelector selectedTags={newThoughtTags} onTagChange={setNewThoughtTags} placeholder="Add tags to organize..." />
              </div>
              <button onClick={addQuickThought} disabled={!newThought.trim()} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {filteredThoughts.map((thought, index) => (
            <div
              key={thought.id}
              draggable
              onDragStart={(e) => handleThoughtDragStart(e, index)}
              onDragOver={handleTaskDragOver}
              onDrop={(e) => handleThoughtDrop(e, index)}
              className={`${themeClasses.cardBg} p-4 rounded-xl shadow-sm ${themeClasses.border} cursor-move hover:shadow-md transition-all`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <GripVertical className={`w-4 h-4 ${themeClasses.textSecondary} cursor-grab mt-1`} />
                  <div className="flex-1">
                    <p className={`${themeClasses.text} mb-2`}>{thought.text}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {thought.tags.map(tag => (
                          <span key={tag} className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 px-2 py-1 rounded-full">{tag}</span>
                        ))}
                      </div>
                      <p className={`text-xs ${themeClasses.textSecondary}`}>{formatDate(thought.timestamp)}</p>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button onClick={() => convertThoughtToTask(thought)} className="text-sky-500 hover:text-sky-700 p-1 transition-colors" title="Convert to task">
                    <Target className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteThought(thought.id)} className={`${themeClasses.textSecondary} hover:text-red-500 p-1 transition-colors`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredThoughts.length === 0 && (
          <div className="text-center py-12">
            <Brain className={`w-16 h-16 ${themeClasses.textSecondary} mx-auto mb-4`} />
            <p className={themeClasses.textSecondary}>
              {selectedTagFilter ? `No thoughts found with tag "${selectedTagFilter}"` : 'No thoughts yet. Capture what\'s on your mind!'}
            </p>
          </div>
        )}
      </div>
    );
  };
  
  const renderChat = () => {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-sky-500 to-cyan-600 rounded-full flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className={`text-xl font-bold ${themeClasses.text}`}>Waddle</h2>
              <p className={`text-sm ${themeClasses.textSecondary}`}>Your Personal Coach</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className={`text-sm ${themeClasses.textSecondary}`}>Online</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 mb-6 p-2" style={{ maxHeight: '500px' }}>
          {chatMessages.map((message) => (
            <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${ message.sender === 'user' ? 'bg-gradient-to-r from-sky-600 to-cyan-600 text-white' : `${themeClasses.cardBg} ${themeClasses.border} ${themeClasses.text}` }`}>
                <p className="text-sm">{message.text}</p>
                {message.suggestions && message.suggestions.length > 0 && message.sender === 'ai' && (
                  <div className="mt-3 space-y-1">
                    {message.suggestions.map((suggestion: string, index: number) => (
                      <button key={index} onClick={() => { setNewMessage(suggestion); }} className="block w-full text-left px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors">
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {isAITyping && (
            <div className="flex justify-start">
              <div className={`${themeClasses.cardBg} ${themeClasses.border} ${themeClasses.text} px-4 py-3 rounded-2xl`}>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={chatEndRef} />
        </div>

        <div className={`${themeClasses.cardBg} p-4 rounded-2xl shadow-sm ${themeClasses.border}`}>
          <div className="flex space-x-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Ask Waddle anything..."
              className={`flex-1 px-4 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500`}
              onKeyPress={(e) => e.key === 'Enter' && handleMainChatSend()}
              disabled={isAITyping}
            />
            <button onClick={handleMainChatSend} disabled={isAITyping || !newMessage.trim()} className="bg-gradient-to-r from-sky-600 to-cyan-600 text-white px-6 py-3 rounded-xl hover:from-sky-700 hover:to-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  };
  
    const Duck = ({ variety = 0, size = 24 }) => {
    const styles = [
        { body: '#FFD700', head: '#FFD700', beak: '#FFA500', eye: '#2c2c2c', wing: 'rgba(255,255,255,0.3)' },
        { body: '#A52A2A', head: '#A52A2A', beak: '#FFA500', eye: '#2c2c2c', wing: 'rgba(255,255,255,0.2)' },
        { body: '#FFFFFF', head: '#FFFFFF', beak: '#FFA500', eye: '#2c2c2c', wing: 'rgba(0,0,0,0.1)' },
        { body: '#4CAF50', head: '#006400', beak: '#FFA500', eye: '#2c2c2c', wing: 'rgba(255,255,255,0.2)' }
    ];
    const s = styles[variety % styles.length];

    return (
        <svg width={size} height={size} viewBox="0 0 24 24" style={{ imageRendering: 'pixelated' }}>
            <rect x="7" y="11" width="10" height="6" fill={s.body} />
            <rect x="9" y="9" width="6" height="4" fill={s.head} />
            <rect x="13" y="10" width="2" height="1" fill={s.eye} />
            <rect x="15" y="11" width="3" height="2" fill={s.beak} />
            <rect x="6" y="13" width="1" height="3" fill={s.wing} />
        </svg>
    );
  };

  const renderPond = () => {
    return (
        <div className="space-y-6">
            <div className="text-center">
                <h2 className={`text-2xl font-bold ${themeClasses.text}`}>The Pond</h2>
                <p className={themeClasses.textSecondary}>A visual log of your daily accomplishments. Each duck represents a day of hard work!</p>
            </div>
            <div className={`${themeClasses.cardBg} rounded-2xl p-4 shadow-sm ${themeClasses.border} h-[60vh] relative overflow-hidden bg-gradient-to-b from-sky-200 to-sky-400`}>
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-sky-600 to-sky-500" />
                {pondDucks.map((duck, index) => {
                    const size = 24 + duck.tasksCompleted * 4;
                    const x = (index * 60 + 20) % (document.body.clientWidth * 0.6 - size);
                    const y = 200 - (duck.tasksCompleted * 5) + (index % 3) * 30;

                    return (
                        <div key={duck.id} className="absolute transition-all duration-500" style={{ bottom: `${y}px`, left: `${x}px` }}>
                            <div className="relative group">
                                <Duck variety={duck.variety} size={size} />
                                <div className="absolute bottom-full mb-2 w-max bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {formatDateOnly(duck.id)}: {duck.tasksCompleted} tasks done!
                                </div>
                            </div>
                        </div>
                    );
                })}
                 {pondDucks.length === 0 && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white">
                        <Waves className="w-24 h-24 opacity-50 mb-4" />
                        <p className="font-bold text-lg">Your pond is quiet.</p>
                        <p className="opacity-80">Complete a task to add your first duck!</p>
                    </div>
                )}
            </div>
        </div>
    );
  };
  
    const renderBodyDouble = () => {
        const formatTime = (seconds: number) => {
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        };

        if (!isBodyDoubleActive) {
            return (
                <div className="text-center flex flex-col items-center justify-center h-full">
                    <h2 className={`text-2xl font-bold ${themeClasses.text}`}>Body Double</h2>
                    <p className={`mt-2 mb-6 max-w-md ${themeClasses.textSecondary}`}>
                        Start a focused work session with Waddle. Your AI companion will keep you company and help you stay on track.
                    </p>
                    <div className={`${themeClasses.cardBg} p-6 rounded-2xl shadow-sm ${themeClasses.border} w-full max-w-sm`}>
                         <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>What are you working on?</label>
                         <select
                            value={bodyDoubleTask}
                            onChange={(e) => setBodyDoubleTask(e.target.value)}
                            className={`w-full px-3 py-2 mb-4 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`}
                         >
                            <option value="">-- Select a task (optional) --</option>
                            {tasks.filter(t => !t.completed).map(task => (
                                <option key={task.id} value={task.text}>{task.text}</option>
                            ))}
                         </select>
                         <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>Session Duration (minutes)</label>
                         <input
                            type="number"
                            value={bodyDoubleTimer}
                            onChange={(e) => setBodyDoubleTimer(parseInt(e.target.value))}
                            min="5"
                            max="120"
                            className={`w-full px-3 py-2 mb-4 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500`}
                         />
                         <button onClick={startBodyDouble} className="w-full bg-gradient-to-r from-sky-600 to-cyan-600 text-white font-bold py-3 px-4 rounded-lg">
                            Start Session
                         </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-4 pb-4 border-b ${themeClasses.border}">
                    <div>
                        <h2 className={`text-xl font-bold ${themeClasses.text}`}>Body Double Session</h2>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>Waddle is here with you.</p>
                    </div>
                    <div className="text-center">
                        <div className={`text-2xl font-mono font-bold ${themeClasses.text}`}>{formatTime(bodyDoubleTimeLeft)}</div>
                        <button onClick={stopBodyDouble} className="text-sm text-red-500 hover:text-red-700">End Session</button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto space-y-4 mb-4 p-2" style={{ maxHeight: '500px' }}>
                    {bodyDoubleMessages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${msg.sender === 'user' ? 'bg-gradient-to-r from-sky-600 to-cyan-600 text-white' : `${themeClasses.cardBg} ${themeClasses.border} ${themeClasses.text}`}`}>
                                <p className="text-sm">{msg.text}</p>
                            </div>
                        </div>
                    ))}
                    {isBodyDoubleAiTyping && (
                        <div className="flex justify-start">
                          <div className={`${themeClasses.cardBg} ${themeClasses.border} ${themeClasses.text} px-4 py-3 rounded-2xl`}>
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                            </div>
                          </div>
                        </div>
                    )}
                    <div ref={bodyDoubleChatEndRef} />
                </div>

                <div className={`${themeClasses.cardBg} p-4 rounded-2xl shadow-sm ${themeClasses.border}`}>
                    <div className="flex space-x-3">
                        <input
                          type="text"
                          value={bodyDoubleUserInput}
                          onChange={(e) => setBodyDoubleUserInput(e.target.value)}
                          placeholder="Talk to Waddle..."
                          className={`flex-1 px-4 py-3 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500`}
                          onKeyPress={(e) => e.key === 'Enter' && handleBodyDoubleSend()}
                          disabled={isBodyDoubleAiTyping}
                        />
                        <button onClick={handleBodyDoubleSend} disabled={isBodyDoubleAiTyping || !bodyDoubleUserInput.trim()} className="bg-gradient-to-r from-sky-600 to-cyan-600 text-white px-6 py-3 rounded-xl hover:from-sky-700 hover:to-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                          <Send className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

  const renderFocusModeModal = () => {
    if (!focusTask) return null;

    const totalSeconds = initialTimerDuration;
    const remainingSeconds = timerMinutes * 60 + timerSeconds;
    const progress = totalSeconds > 0 ? ((totalSeconds - remainingSeconds) / totalSeconds) * 100 : 0;
    
    return (
      <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
        <div className={`w-full max-w-2xl ${themeClasses.cardBg} rounded-3xl p-8 shadow-2xl relative`}>
          <button onClick={endFocusSession} className={`absolute top-4 right-4 ${themeClasses.textSecondary} hover:${themeClasses.text}`}><X/></button>
          
          <h2 className="text-2xl font-bold text-center mb-2">Focusing on:</h2>
          <p className="text-center text-lg text-sky-500 mb-6">{focusTask.text}</p>
          
          <div className="relative mb-6">
              <div className="text-center text-6xl font-mono font-bold mb-4">{String(timerMinutes).padStart(2, '0')}:{String(timerSeconds).padStart(2, '0')}</div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                  <div className="bg-gradient-to-r from-green-400 to-sky-500 h-4 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
              </div>
          </div>
          
          <div className="flex justify-center space-x-4 mb-6">
              <button onClick={toggleTimer} className="bg-sky-600 hover:bg-sky-700 text-white p-3 rounded-full">{isTimerActive ? <Pause className="w-6 h-6"/> : <Play className="w-6 h-6"/>}</button>
              <button onClick={resetTimer} className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 p-3 rounded-full"><RotateCcw className="w-6 h-6"/></button>
          </div>

          <div className={`border-t ${themeClasses.border} pt-4`}>
              <h3 className="font-bold mb-3 text-center">Actionable Steps</h3>
              {isBreakingDownTask ? <p className="text-center">Waddle is breaking it down...</p> : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                      {focusSubTasks.map(st => (
                          <div key={st.id} onClick={() => toggleSubTask(st.id)} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${st.completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 dark:border-gray-600'}`}>{st.completed && <Check className="w-4 h-4 text-white"/>}</div>
                              <span className={st.completed ? `line-through ${themeClasses.textSecondary}` : themeClasses.text}>{st.text}</span>
                          </div>
                      ))}
                  </div>
              )}
          </div>
        </div>
      </div>
    );
  };
  
  const renderSettings = () => {
    return (
      <div className="space-y-8 max-w-2xl mx-auto">
        <div className="text-center">
            <h2 className={`text-2xl font-bold ${themeClasses.text}`}>Settings</h2>
            <p className={themeClasses.textSecondary}>Customize your Waddle It experience.</p>
        </div>

        {/* Appearance Settings */}
        <div className={`${themeClasses.cardBg} p-6 rounded-2xl shadow-sm ${themeClasses.border}`}>
          <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>Appearance</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label htmlFor="darkModeToggle" className={`${themeClasses.text}`}>Enable Dark Mode</label>
              <button onClick={() => setUserSettings(s => ({...s, enableDarkMode: !s.enableDarkMode}))} className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${userSettings.enableDarkMode ? 'bg-sky-600' : 'bg-gray-200'}`}>
                <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${userSettings.enableDarkMode ? 'translate-x-6' : 'translate-x-1'}`}/>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4">
                <div>
                    <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Date Format</label>
                    <select value={userSettings.dateFormat} onChange={(e) => setUserSettings(s => ({...s, dateFormat: e.target.value as any}))} className={`w-full px-3 py-2 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg`}>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    </select>
                </div>
                <div>
                    <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Time Format</label>
                    <select value={userSettings.timeFormat} onChange={(e) => setUserSettings(s => ({...s, timeFormat: e.target.value as any}))} className={`w-full px-3 py-2 ${themeClasses.border} ${themeClasses.cardBg} ${themeClasses.text} rounded-lg`}>
                        <option value="12h">12-Hour</option>
                        <option value="24h">24-Hour</option>
                    </select>
                </div>
            </div>
          </div>
        </div>
        
        {/* Effects Settings */}
        <div className={`${themeClasses.cardBg} p-6 rounded-2xl shadow-sm ${themeClasses.border}`}>
          <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>Effects</h3>
          <div className="space-y-4">
             <div className="flex items-center justify-between">
              <label htmlFor="confettiToggle" className={`${themeClasses.text}`}>Enable Confetti</label>
              <button onClick={() => setUserSettings(s => ({...s, enableConfetti: !s.enableConfetti}))} className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${userSettings.enableConfetti ? 'bg-sky-600' : 'bg-gray-200'}`}>
                <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${userSettings.enableConfetti ? 'translate-x-6' : 'translate-x-1'}`}/>
              </button>
            </div>
             <div className="flex items-center justify-between">
              <label htmlFor="soundsToggle" className={`${themeClasses.text}`}>Enable Sounds</label>
              <button onClick={() => setUserSettings(s => ({...s, enableSounds: !s.enableSounds}))} className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${userSettings.enableSounds ? 'bg-sky-600' : 'bg-gray-200'}`}>
                <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${userSettings.enableSounds ? 'translate-x-6' : 'translate-x-1'}`}/>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };


  if (!isLoggedIn) {
    return renderAuthModal();
  }

  return (
    <div className={`min-h-screen ${themeClasses.bg} transition-colors duration-300`}>
      {userSettings.enableConfetti && showConfetti && <ConfettiComponent onComplete={() => setShowConfetti(false)} />}
      
      {renderPopups()}
      {renderPersistentTimer()}
      {showTimerSettings && renderTimerSettingsModal()}
      {showLocationPrompt && renderLocationPromptModal()}
      {focusTask && renderFocusModeModal()}

      <div className="flex">
        <div className={`w-64 ${themeClasses.sidebarBg} shadow-lg h-screen sticky top-0 transition-colors duration-300`}>
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-8">
              <img src="/favicon.ico" alt="Waddle It Logo" className="w-10 h-10" />
              <div>
                <h1 className={`text-lg font-bold ${themeClasses.text}`}>Waddle It</h1>
                <p className={`text-sm ${themeClasses.textSecondary}`}>Your Companion</p>
              </div>
            </div>

            <nav className="space-y-2">
              {[
                { id: 'dashboard', label: 'Dashboard', icon: Home },
                { id: 'tasks', label: 'Tasks', icon: Target },
                { id: 'pond', label: 'The Pond', icon: Waves },
                { id: 'bodydouble', label: 'Body Double', icon: MessageCircle },
                { id: 'focus', label: 'Focus Timer', icon: Clock },
                { id: 'braindump', label: 'Brain Dump', icon: Brain },
                { id: 'vault', label: 'The Vault', icon: ShieldCheck },
                { id: 'chat', label: 'AI Coach', icon: MessageSquare },
                { id: 'settings', label: 'Settings', icon: Settings }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-sky-600 to-cyan-600 text-white shadow-lg'
                      : `${themeClasses.textSecondary} hover:bg-gray-100 dark:hover:bg-gray-700`
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className={`absolute bottom-0 left-0 right-0 p-6 border-t ${themeClasses.border}`}>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              </div>
              <div>
                <p className={`text-sm font-medium ${themeClasses.text}`}>{user?.name}</p>
                <p className={`text-xs ${themeClasses.textSecondary}`}>{user?.email}</p>
              </div>
            </div>
            <button onClick={handleLogout} className={`w-full text-left text-sm ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors`}>
              Sign out
            </button>
          </div>
        </div>

        <main className="flex-1 p-8 overflow-y-auto" style={{ height: '100vh' }}>
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'tasks' && renderTasks()}
          {activeTab === 'pond' && renderPond()}
          {activeTab === 'bodydouble' && renderBodyDouble()}
          {activeTab === 'focus' && renderFocusTimer()}
          {activeTab === 'braindump' && renderBrainDump()}
          {activeTab === 'vault' && <VaultComponent items={vaultItems} onAdd={addVaultItem} onDelete={deleteVaultItem} themeClasses={themeClasses} formatDate={formatDate} />}
          {activeTab === 'chat' && renderChat()}
          {activeTab === 'settings' && renderSettings()}
        </main>
      </div>
      <style>{`
        .bg-eggshell { background-color: #FDFBF5; }
        .bg-beige-card { background-color: #FAF8F1; }
        .dark .bg-beige-card { background-color: #1f2937; } /* Corresponds to dark:bg-gray-800 */
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .dark ::-webkit-scrollbar-thumb { background: #4b5563; }
      `}</style>
    </div>
  );
};

export default ADHDAIAssistant;

